/**
 * A password box with a button to show what has been typed.
 *
 * Emmanuel's review, 13 October 2026: "when typing the password, I should be
 * able to click something to view what I am typing". A long passphrase typed
 * blind is exactly where mistakes happen, and a wrong password that looked
 * right is a locked-out owner.
 *
 * The button is not a tab stop: someone tabbing from password to confirm should
 * land on confirm. It stays reachable by pointer and touch, and the state is
 * announced through `aria-pressed`.
 */
import { useState, type InputHTMLAttributes } from 'react';
import { c } from '../theme.js';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** What the box holds, for the button's label: "password" or "PIN". */
  what?: string;
};

export function PasswordInput({ what = 'password', style, ...rest }: Props) {
  const [shown, setShown] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        {...rest}
        type={shown ? 'text' : 'password'}
        style={{ ...style, paddingRight: 46 }}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-pressed={shown}
        aria-label={shown ? `Hide ${what}` : `Show ${what}`}
        title={shown ? `Hide ${what}` : `Show ${what}`}
        onClick={() => setShown((v) => !v)}
        style={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: '50%',
          background: 'transparent',
          color: shown ? c.green : c.faint,
          cursor: 'pointer',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M1.8 10S4.8 4.5 10 4.5 18.2 10 18.2 10 15.2 15.5 10 15.5 1.8 10 1.8 10z" />
          <circle cx="10" cy="10" r="2.4" />
          {/* A stroke through the eye while the text is hidden. */}
          {!shown && <path d="M3.5 3.500l13 13" />}
        </svg>
      </button>
    </div>
  );
}
